import React from 'react';
import PropTypes from 'prop-types';

import { useStyles } from '~/assets/styles';
import Grid from '~/components/core/Atomic/Grid/Grid';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import GrossVatTooltip from '~/components/Finances/GrossVatTooltip';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { useVatLabel } from '~/components/hooks/useVatLabel';
import { getDeductibleText } from '~/Utils/deductibleUtils';

import { useClaim } from '../../../ClaimContainer';
import { SortableTable } from '../../../core';

import SumsHeader from './SumsHeader';

import styles from '../financesScreen.module.scss';

const SummaryTab = ({ data, isLoading, isError }) => {
  const { classes } = useStyles();
  const { claim } = useClaim();
  const { currencyFormatter } = useCurrencyFormatter();
  const { includeVatInPaymentScreen, includeVatInFinancesSummary, displayNetAmounts } = usePaymentsConfiguration();
  const vatLabel = useVatLabel();
  const displayLoading = isLoading || isError;

  const isWcClaim = claim.type === 'wc_claim';

  // Only surface the VAT breakdown when VAT is actually used by the org (payment-screen VAT),
  // not just when the finances-summary toggle is on — otherwise VAT is always 0 and the columns
  // (and the Gross Paid relabel) are meaningless.
  const showVatColumns = includeVatInPaymentScreen && includeVatInFinancesSummary;

  const currencyCell = currencyFormatter.format;

  let columnsData = [
    {
      id: 'exposure_name',
      label: 'Exposure Name',
      disableSort: true,
    },
    {
      id: 'payable_type',
      label: 'Payable Type',
      disableSort: true,
    },
    {
      id: 'paid_sum',
      label: showVatColumns ? 'Gross Paid' : 'Paid to Date',
      disableSort: true,
      specialCell: (row) => currencyCell(row.paid_sum),
    },
    {
      id: 'reserve',
      label: 'Reserve to Date',
      disableSort: true,
      specialCell: (row) => currencyCell(row.reserve),
    },
    {
      id: 'deductible_applied',
      label: `${getDeductibleText()} Applied`,
      disableSort: true,
      specialCell: (row) => (row.deductible_applied === null ? '' : currencyCell(row.deductible_applied)),
    },
    {
      id: 'recoveries',
      label: displayNetAmounts ? 'Net Recoveries' : 'Recoveries',
      disableSort: true,
      // With the toggle on the column shows the recoveries net of the VAT/GST captured on them - the
      // same figure the Net Incurred column subtracts - and the gross amount recorded moves into the
      // hover hint. `net_recoveries` is null on the rows the server did not compute it for, so those
      // fall back to the gross figure (and then have no VAT/GST to disclose).
      specialCell: (row) => {
        if (row.recoveries === null || row.recoveries === undefined) {
          return '';
        }
        if (!displayNetAmounts) {
          return currencyCell(row.recoveries);
        }

        const netRecoveries = row.net_recoveries ?? row.recoveries;

        return (
          <GrossVatTooltip grossAmount={row.recoveries} vatAmount={row.recoveries - netRecoveries}>
            {currencyCell(netRecoveries)}
          </GrossVatTooltip>
        );
      },
    },
    {
      id: 'incurred',
      label: displayNetAmounts ? 'Net Incurred' : 'Incurred',
      disableSort: true,
      // `net_incurred` is null on the rows the server did not compute it for, so fall back to the
      // gross incurred figure instead of leaving the cell blank.
      specialCell: (row) => currencyCell(displayNetAmounts ? (row.net_incurred ?? row.incurred) : row.incurred),
    },
  ];

  if (data?.includes_sub_reserves) {
    columnsData.splice(2, 0, {
      id: 'sub_reserve_name',
      label: 'Sub Reserve Type',
      disableSort: true,
    });
  }

  // WC Claim doesn't have expenses per each exposure
  if (isWcClaim) {
    columnsData.splice(
      columnsData.findIndex(({ id }) => id === 'payable_type'),
      1
    );
  }

  const paidSumIndex = columnsData.findIndex(({ id }) => id === 'paid_sum');
  if (showVatColumns && paidSumIndex !== -1) {
    columnsData.splice(
      paidSumIndex + 1,
      0,
      {
        id: 'net_paid',
        label: 'Net Paid',
        disableSort: true,
        specialCell: (row) => (row.net_paid === null || row.net_paid === undefined ? '' : currencyCell(row.net_paid)),
      },
      {
        id: 'total_vat',
        label: `Total ${vatLabel}`,
        disableSort: true,
        specialCell: (row) =>
          row.total_vat === null || row.total_vat === undefined ? '' : currencyCell(row.total_vat),
      }
    );
  }

  return (
    <Grid container>
      <Grid item md={12}>
        <div className={classes.cardDivRow}>
          {displayLoading && <SkeletonTable rowsCount={5} columnsCount={columnsData?.length || 7} isError={isError} />}
          {!displayLoading && (
            <>
              <div className={styles.sumsHeader}>
                {/* WC Claim doesn't have expenses per each exposure */}
                <SumsHeader amounts={data} shouldDisplayOnlyHeader={isWcClaim} displayNetAmounts={displayNetAmounts} />
              </div>
              <SortableTable
                rows={data?.rows || []}
                columns={columnsData}
                subRowsPropertyName="sub_rows"
                boldRowsIds={data?.rows.map((r) => r.id) || []}
              />
            </>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

SummaryTab.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

export default SummaryTab;
